import React, { useEffect, useRef, useState } from 'react';
import styles from './AppointmentCell.module.css';
import { ReactSVG } from 'react-svg';
import { Icons } from '@/assets';
import ActionButton from '../ActionButton/ActionButton';
import { availableApointmentTimes, SaveApiResMessage } from '@/actions';
import Modal from '@/ctComponents/Modal';
import LicensePlateModal from '../LicensePlateModal';
import CloseAlert from '../CloseAlert';
import { noScroll, removeNoScroll } from '@/actions/Helper';
import { createPortal } from 'react-dom';
import {
  disableAutoBookScroll,
  enableAutoBookScroll,
} from '@/utils/autobook-scroll';

const ActionButtonPortal = ({ children, isOpen, position, dropdownRef }) => {
  if (!isOpen) return null;

  return createPortal(
    <div
      ref={dropdownRef}
      style={{
        position: 'absolute',
        zIndex: 99,
        ...position,
      }}
      onClick={(e) => e.stopPropagation()}>
      {children}
    </div>,
    document.body
  );
};

const AppointmentCell = ({
  data,
  appset,
  cabinId,
  row,
  cell,
  index,
  appointmentSet,
  containerId,
  setAppointmentSet,
  containerName,
  selectedData,
  setSelectedData,
  ContainerOptions,
  Container,
  SSL,
  Size,
  selectedStatus,
  setSelected,
  isAutobookPage,
}) => {
  const wrapperRef = useRef(null);
  const closedAlertRef = useRef(null);
  const dropdownRef = useRef(null);

  const [display, setDisplay] = useState(false);
  const [showClosedAlert, setShowClosedAlert] = useState(false);
  const [show, setShow] = useState(false);
  const [clickedCell, setClickedCell] = useState();
  const [position, setPosition] = useState({
    bottom: 0,
    left: '100%',
    right: 'auto',
  });
  const [cellData, setCellData] = useState({
    data: [],
    shift: '',
    terminalSlug: '',
    date: '',
    lastUpdate: '',
  });

  useEffect(() => {
    show ? noScroll() : removeNoScroll();
  }, [show]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        display &&
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        removeNoScroll();
        setDisplay(false);
        enableAutoBookScroll();
      }

      if (
        showClosedAlert &&
        closedAlertRef.current &&
        !closedAlertRef.current.contains(event.target)
      ) {
        setShowClosedAlert(false);
      }
    };

    if (display || showClosedAlert) {
      document.addEventListener('mousedown', handleClickOutside);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [display, showClosedAlert, clickedCell]);

  useEffect(() => {
    const handleResizeOrOrientationChange = () => {
      removeNoScroll();
      setDisplay(false);
      setShow(false);
      enableAutoBookScroll();
    };

    window.addEventListener(
      'orientationchange',
      handleResizeOrOrientationChange
    );
    window.addEventListener('resize', handleResizeOrOrientationChange);

    return () => {
      window.removeEventListener(
        'orientationchange',
        handleResizeOrOrientationChange
      );
      window.removeEventListener('resize', handleResizeOrOrientationChange);
    };
  }, [display, show]);

  const handleCellClick = () => {
    availableApointmentTimes({
      cabinId: cabinId,
      terminalId: row.id,
      date: cell.date,
      shift: index + 1,
      containerName: Container?.label || containerName,
    })
      .then((res) => {
        setCellData({
          data: res?.availabilities,
          shift: index + 1,
          terminalSlug: row.slug,
          date: cell.date,
          lastUpdate: res.last_update,
        });
      })
      .catch((err) => {
        // notify(err, 'warning');
        SaveApiResMessage({ title: 'Error', message: err, mode: 'warning' });
      });
  };

  const calculatePosition = (event, isClosedAlert = false) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setClickedCell(event.currentTarget);

    const scrollY = window.pageYOffset || document.documentElement.scrollTop;

    const minRequiredSpace = isClosedAlert
      ? 416
      : window.innerWidth <= 1440
      ? 426
      : 533;

    const isMobile = window.innerWidth <= 800;

    const isHorizontal =
      window.innerWidth < 950 && window.innerWidth > window.innerHeight;

    const spaceFromToTop = rect.bottom;

    return {
      ...(isHorizontal
        ? {
            bottom: 0,
          }
        : spaceFromToTop > minRequiredSpace
        ? {
            bottom: window.innerHeight - rect.bottom - scrollY,
            transform:
              window.innerHeight - spaceFromToTop > minRequiredSpace * 0.2
                ? 'translateY(20%)'
                : 'translateY(0%)',
          }
        : isAutobookPage &&
          window.innerHeight - spaceFromToTop - scrollY <= minRequiredSpace
        ? {
            bottom: 0,
            transform: 'translateY(-20%)',
          }
        : {
            top: rect.top + scrollY,
            transform: 'translateY(-20%)',
          }),
      left:
        window.innerWidth - rect.right > minRequiredSpace
          ? isMobile || isClosedAlert
            ? '0px'
            : rect.right
          : 'auto',
      right:
        window.innerWidth - rect.right > minRequiredSpace
          ? 'auto'
          : isMobile || isClosedAlert
          ? '0px'
          : window.innerWidth - rect.left,
    };
  };

  const handleDisplay = (event) => {
    disableAutoBookScroll();
    noScroll(!isAutobookPage ? true : false);
    setPosition(calculatePosition(event));
    setDisplay(true);
    setShowClosedAlert(false); // Close closed alert when opening main dropdown
    data?.available_count > 0 && handleCellClick();
  };

  const handleClosedDisplay = (event) => {
    setPosition(calculatePosition(event, true));
    setShowClosedAlert(true);
    removeNoScroll();
    setDisplay(false); // Close main dropdown when opening closed alert
  };

  const handleClosedClose = () => {
    setShowClosedAlert(false);
  };

  if (!data) {
    return (
      <div className={styles.card} style={{ borderColor: '#99A0AE66' }}>
        <div className={styles.flexCard}>
          <span className={styles.text} style={{ color: '#717784' }}>
            No Data
          </span>
        </div>
      </div>
    );
  } else if (data?.single === 0 && data?.available_count === 0) {
    return (
      <div
        className={styles.card}
        style={{ borderColor: '#FB374866' }}
        onClick={(event) => handleClosedDisplay(event)}>
        <div className={styles.flexCard}>
          <span className={styles.text} style={{ color: '#FB3748' }}>
            Closed
          </span>
        </div>
        {showClosedAlert && (
          <div
            onClick={(e) => e.stopPropagation()}
            ref={closedAlertRef}
            style={{
              position: 'absolute',
              zIndex: 99,
              bottom: 0,
              left: position.left,
              right: position.right,
            }}>
            <CloseAlert
              time={row?.updated_at}
              handleClose={handleClosedClose}
            />
          </div>
        )}
      </div>
    );
  } else {
    return (
      <div
        ref={wrapperRef}
        className={appset ? styles.setContainer : styles.card}
        style={{
          borderColor: appset
            ? '#335CFF33'
            : data?.available_count === 0
            ? '#99A0AE66'
            : data?.single === 0 || data.single === 2
            ? '#f5aab2'
            : '#8ee7b8',
          backgroundColor: appset ? '#335CFF' : '#FFFFFF',
          ...((row.refreshing_slots || row?.blur_status === 1) && {
            boxShadow: 'none',
          }),
        }}
        onClick={(event) => data?.available_count > 0 && handleDisplay(event)}>
        {!appset && data?.available_count > 0 && data?.single === 1 ? (
          <div
            className={styles.leftBar}
            style={{
              backgroundColor:
                data?.available_count === 0 ? '#F6B51E' : '#1FC16B',
            }}
          />
        ) : data?.single === 0 || data.single === 2 ? (
          <div
            className={styles.leftBar}
            style={{
              backgroundColor: '#f5aab2',
            }}
          />
        ) : null}

        <div className={styles.cardBody}>
          <div className={styles.row}>
            {data?.single === 1 ? (
              <span
                className={styles.title}
                style={{
                  color: appset
                    ? 'white'
                    : data?.available_count === 0
                    ? '#99A0AE'
                    : '#1FC16B',
                }}>
                Open
              </span>
            ) : data.single === 0 || data.single === 2 ? (
              <span
                className={styles.title}
                style={{
                  color: appset
                    ? 'white'
                    : data?.available_count === 0
                    ? '#99A0AE'
                    : '#FB3748',
                }}>
                {data.single === 2 ? 'Dual' : 'Close'}
              </span>
            ) : (
              <span className={styles.title}>{''}</span>
            )}
            <ReactSVG
              src={Icons.SingleShift}
              className={styles.shiftarrow}
              beforeInjection={(svg) => {
                svg.querySelectorAll('path').forEach((path) => {
                  path.setAttribute('stroke', appset ? '#FFFFFF' : '#525866');
                });
              }}
            />
            <ReactSVG
              src={Icons.DualShift}
              className={styles.shiftarrow}
              beforeInjection={(svg) => {
                svg.querySelectorAll('path').forEach((path) => {
                  path.setAttribute('stroke', appset ? '#FFFFFF' : '#525866');
                });
              }}
            />
          </div>
          <div
            className={styles.row}
            style={{ color: appset ? '#FFFFFF' : '#131517' }}>
            <span className={styles.subtitle}>Avail:</span>
            <span>{data?.available_count || 0}</span>
            <span>0</span>
          </div>
          <div
            className={styles.row}
            style={{ color: appset ? '#FFFFFF' : '#131517' }}>
            <span>Booked:</span>
            <span>{data?.booked_count || 0}</span>
            <span className={styles.zero}>0</span>
          </div>
        </div>
        <ActionButtonPortal
          isOpen={display}
          position={position}
          dropdownRef={dropdownRef}>
          <div onClick={(e) => e.stopPropagation()}>
            <ActionButton
              data={cellData.data}
              setDisplay={setDisplay}
              shift={cellData?.shift}
              terminal={cellData?.terminalSlug}
              date={cellData.date}
              lastUpdate={cellData.lastUpdate}
              setSelectedData={setSelectedData}
              setShow={setShow}
              selectedData={selectedData}
              appset={
                appointmentSet !== undefined &&
                appointmentSet !== null &&
                appointmentSet.status !== 'CANCELED' &&
                appointmentSet.status !== 'RESERVATION_FAILED' &&
                appointmentSet.status !== 'REJECTED' &&
                appointmentSet.selectId
              }
              edit={
                appointmentSet !== undefined &&
                appointmentSet !== null &&
                appointmentSet.status !== 'CANCELED' &&
                appointmentSet.status !== 'PENDING_CANCELED' &&
                appointmentSet.status !== 'RESERVATION_FAILED' &&
                appointmentSet.status !== 'REJECTED' &&
                appointmentSet.status !== 'EXPIRED'
                  ? true
                  : false
              }
              status={
                appointmentSet &&
                appointmentSet[row?.id] &&
                appointmentSet.status
              }
              selectedStatus={selectedStatus}
            />
          </div>
        </ActionButtonPortal>

        <Modal
          show={show}
          notClose={true}
          children={
            <div className="w-full" onClick={(e) => e.stopPropagation()}>
              <LicensePlateModal
                terminalId={row.id}
                setShow={setShow}
                ContainerOptions={ContainerOptions}
                Container={Container}
                SSL={SSL}
                Size={Size}
                selectedData={selectedData}
                cellData={cellData}
                containerId={containerId}
                setAppointmentSet={setAppointmentSet}
                edit={
                  typeof appointmentSet === 'object' &&
                  appointmentSet !== undefined &&
                  appointmentSet !== null &&
                  appointmentSet.status !== 'CANCELED' &&
                  appointmentSet.status !== 'PENDING_CANCELED' &&
                  appointmentSet.status !== 'RESERVATION_FAILED' &&
                  appointmentSet.status !== 'REJECTED' &&
                  appointmentSet.status !== 'EXPIRED'
                    ? true
                    : false
                }
                setDisplay={setDisplay}
                containerName={containerName}
                licensePlate={
                  (appointmentSet && appointmentSet.license_plate) || ''
                }
                selectedStatus={selectedStatus}
                containerAppointmentId={appointmentSet && appointmentSet.id}
                setSelected={setSelected}
              />
            </div>
          }
          icon={'appointmentBlack'}
          title={
            appointmentSet &&
            appointmentSet !== null &&
            appointmentSet?.status !== 'CANCELED' &&
            appointmentSet?.status !== 'PENDING_CANCELED' &&
            appointmentSet?.status !== 'REJECTED' &&
            appointmentSet?.status !== 'EXPIRED'
              ? 'Reschedule Appointment'
              : 'Book Appointment'
          }
          setShow={setShow}
        />
      </div>
    );
  }
};

export default AppointmentCell;
